import { useReducedMotion } from 'motion/react';
import { useEffect, useState } from 'react';

// Cadence matches Carvana.Composer.UI's own intro-screen rotation
// (src/hooks/useTypewriterRotation.ts) so both surfaces feel consistent.
const TYPE_TICK_MS = 40;
const HOLD_MS = 2200;

type ReturnType = {
  displayedText: string;
  targetQuestion: string;
};

// `questions` must be a stable reference (e.g. a module-level constant) — it's an
// effect dependency below, so a new array identity on every render would reset
// the timer each time and the animation would never advance.
export default function useComposerStarterQuestionTypewriter(
  questions: ReadonlyArray<string>,
  enabled = true
): ReturnType {
  const prefersReducedMotion = useReducedMotion();
  const [questionIndex, setQuestionIndex] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const targetQuestion = questions[questionIndex];

  useEffect(() => {
    if (prefersReducedMotion || !enabled) return undefined;

    const isFullyTyped = charCount >= targetQuestion.length;

    const timeoutId = setTimeout(
      () => {
        if (isFullyTyped) {
          setCharCount(0);
          setQuestionIndex(previous => (previous + 1) % questions.length);
        } else {
          setCharCount(previous => previous + 1);
        }
      },
      isFullyTyped ? HOLD_MS : TYPE_TICK_MS
    );

    return () => clearTimeout(timeoutId);
  }, [charCount, enabled, prefersReducedMotion, questions, targetQuestion]);

  return {
    displayedText: prefersReducedMotion
      ? targetQuestion
      : targetQuestion.slice(0, charCount),
    targetQuestion
  };
}
